import {useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Typography} from '@mui/material';
import {useSnackBar} from '../../../../../../contexts/snack-bar/context';
import {useFetchEntityUsers} from '../../../../../../hooks/use-fetch-entity-users';
import {translations} from '../../../../../../i18n/translations';
import {ReminderForm} from '../../../../../../components/reminder-form-dialog/reminder-form-dialog';
import {Modal} from '../../../../../../components/modal/modal';
import {useModalBtns} from '../../../../../../components/modal/use-modal-btns';
import {
  getReminderFormValues,
  ReminderFormValues,
} from '../../../informal-caregiver/reminder/reminder-form.utils';
import {
  SelectOptionValue,
  UsersMultySearchSelectInput,
} from '../../planner/calendar/modals/create/activity/3-planner-activity-users';
import {editOperations, GroupReminder, ReminderOperation, repositoryFor} from './reminder-groups';

export type GroupRow = {reminder: GroupReminder; recipient: string; picture?: string};
type Props = {
  rows: GroupRow[];
  deleting: boolean;
  entityId: string;
  onClose: (changed: boolean) => void;
};

export function GroupReminderModal({rows, deleting, entityId, onClose}: Props) {
  const submitFormRef = useRef<() => Promise<boolean>>();
  const changedRef = useRef(false);
  const close = () => onClose(changedRef.current);
  const [step, setStep] = useState<'details' | 'recipients'>(deleting ? 'recipients' : 'details');
  const currentRecipients: SelectOptionValue[] = rows
    .filter(
      (row, index) => rows.findIndex((r) => r.reminder.toUserId === row.reminder.toUserId) === index
    )
    .map(({reminder, recipient, picture}) => ({
      id: reminder.toUserId,
      name: recipient,
      picture,
      type: 'user',
    }));
  const [selected, setSelected] = useState<SelectOptionValue[]>(currentRecipients);
  const [pending, setPending] = useState<ReminderOperation[]>();
  const [saving, setSaving] = useState(false);
  const [scheduleError, setScheduleError] = useState(false);
  const {t} = useTranslation();
  const snackBar = useSnackBar();
  const {items, isLoading} = useFetchEntityUsers({activeUsers: true});
  const reminder = rows[0].reminder;
  const advanced =
    reminder.recurrenceAdvancedConfiguration ||
    [
      reminder.recurrenceSecondsOfTheMinute,
      reminder.recurrenceMinutesOfTheHour,
      reminder.recurrenceHoursOfTheDay,
      reminder.recurrenceDaysOfTheWeek,
      reminder.recurrenceDaysOfTheMonth,
      reminder.recurrenceMonthsOfTheYear,
    ].some((items) => items?.length);
  const existingIds = new Set(rows.map((row) => row.reminder.toUserId));
  const options: SelectOptionValue[] | undefined = isLoading
    ? undefined
    : [
        ...currentRecipients.filter((option) => !items?.some((item) => item.user.id === option.id)),
        ...(items ?? []).map(
          (item): SelectOptionValue => ({
            id: item.user.id!,
            name: item.user.name,
            picture: item.user.picture,
            socialResponses: item.affiliation.socialResponses,
            type: 'user',
          })
        ),
      ];

  const execute = async (operations: ReminderOperation[]) => {
    setSaving(true);
    const failures: ReminderOperation[] = [];
    for (const operation of operations) {
      try {
        await operation.run();
        changedRef.current = true;
      } catch {
        failures.push(operation);
      }
    }
    setPending(failures);
    setSaving(false);
    void snackBar.setOfficeToastNotification({
      severity: failures.length ? 'error' : 'success',
      message: t(
        failures.length
          ? translations.reminders.group_partial
          : deleting
            ? translations.reminders.delete_success
            : translations.reminders.save_success
      ),
    });
    if (!failures.length) close();
  };
  const submit = async (values: ReminderFormValues) => {
    const original = getReminderFormValues(reminder);
    if (
      advanced &&
      (values.time !== original.time ||
        values.recurrenceType !== original.recurrenceType ||
        values.recurrenceCount !== original.recurrenceCount)
    ) {
      setScheduleError(true);
      setStep('details');
      return;
    }
    setScheduleError(false);
    if (step === 'details') {
      setStep('recipients');
      return;
    }
    const added = selected.map((user) => user.id).filter((id) => !existingIds.has(id));
    const selectedIds = new Set(selected.map((user) => user.id));
    void execute(
      editOperations(
        rows.map((r) => r.reminder),
        values,
        Array.from(existingIds).filter((id) => !selectedIds.has(id)),
        added,
        entityId
      )
    );
  };
  const deleteAll = () =>
    void execute(
      rows.map(({reminder}) => ({run: () => repositoryFor(reminder).deleteById(reminder.id)}))
    );
  const actions = useModalBtns({
    left: {
      type: !deleting && step === 'recipients' && !pending ? 'back' : 'cancel',
      disabled: saving,
      onClick: !deleting && step === 'recipients' && !pending ? () => setStep('details') : close,
    },
    right: {
      type: deleting ? 'delete' : step === 'details' ? 'next' : 'save',
      disabled: saving,
      loading: saving,
      onClick: () => {
        if (pending) void execute(pending);
        else if (deleting) deleteAll();
        else void submitFormRef.current?.();
      },
    },
  });
  if (pending) actions[1].label = t(translations.reminders.retry);

  return (
    <ReminderForm
      reminder={reminder}
      isSubmitting={saving || !!pending}
      onSave={submit}
      onInvalid={() => setStep('details')}
      fieldErrors={(values) => {
        const original = getReminderFormValues(reminder);
        const scheduleMessage = t(translations.reminders.group_advanced);
        return {
          time: scheduleError && values.time !== original.time ? scheduleMessage : undefined,
          recurrenceType:
            scheduleError && values.recurrenceType !== original.recurrenceType
              ? scheduleMessage
              : undefined,
          recurrenceCount:
            scheduleError && values.recurrenceCount !== original.recurrenceCount
              ? scheduleMessage
              : undefined,
        };
      }}
    >
      {(form, submitForm) => {
        submitFormRef.current = submitForm;
        return (
          <Modal
            open
            onClose={saving ? undefined : close}
            title={t(
              deleting ? translations.reminders.group_delete : translations.reminders.group_edit
            )}
            maxWidth="sm"
            fullWidth
            actions={actions}
          >
            {deleting ? (
              <Typography>
                {t(translations.reminders.group_delete_confirm, {
                  title: reminder.title,
                  count: existingIds.size,
                })}
              </Typography>
            ) : (
              <>
                <div style={{display: step === 'details' ? 'block' : 'none'}}>{form}</div>
                {step === 'recipients' && (
                  <UsersMultySearchSelectInput
                    options={options}
                    selectedOptions={selected}
                    disabled={saving || !!pending}
                    onChange={setSelected}
                  />
                )}
                {advanced && step === 'details' && (
                  <Typography variant="caption" color="text.secondary">
                    {t(translations.reminders.group_advanced)}
                  </Typography>
                )}
              </>
            )}
          </Modal>
        );
      }}
    </ReminderForm>
  );
}
